import { useApolloClient, useQuery } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { useToast } from "utils/hooks";
import { emptyFunction } from "constants/emptyConst";
import { useGlobalCacheManager } from "providers/GlobalCacheManager";

export function useQueryWithErrorHandling(query, { onError = emptyFunction, cacheEviction = false, ...props } = {}) {
  const { registerQueryForCacheEviction } = useGlobalCacheManager();
  const showError = useError();
  const errorHandler = useCallback(
    error => {
      onError();
      showError(error);
    },
    [onError, showError]
  );

  useEffect(() => {
    if (cacheEviction) registerQueryForCacheEviction(query);
  }, [query, cacheEviction, registerQueryForCacheEviction]);

  return useQuery(query, {
    ...props,
    onError: errorHandler
  });
}

export function useClientQueryWithErrorHandling() {
  const client = useApolloClient();
  const showError = useError();
  return async (query, props) => {
    try {
      return await client.query({
        query,
        ...props
      });
    } catch (error) {
      showError(error);
      return { loading: false, error };
    }
  };
}

const generateErrorMessage = error => `${error.extensions.code} - ${error.message}`;
const generateSimpleErrorMessage = message => `There is a problem communicating with the server. ${message}`;

export const useError = () => {
  const addToast = useToast();
  const addErrorToast = useCallback(
    message => addToast(generateSimpleErrorMessage(message), "error", false),
    [addToast]
  );
  const addValidationToast = useCallback(message => addToast(message, "error", false), [addToast]);

  return useCallback(
    error => {
      if (!error?.graphQLErrors && !error?.networkError?.result?.errors) {
        addErrorToast(generateSimpleErrorMessage(error.message));
        return;
      }

      const graphQLErrors = error?.graphQLErrors ?? [];
      graphQLErrors.forEach(err => {
        const code = err?.extensions?.code;
        if (code) {
          code.toUpperCase().includes("VALIDATION")
            ? addValidationToast(err.message)
            : addErrorToast(generateErrorMessage(err));
        } else {
          addErrorToast(generateSimpleErrorMessage(err.message));
        }
      });

      const networkErrors = error?.networkError?.result?.errors ?? [];
      networkErrors.forEach(err => addErrorToast(generateErrorMessage(err)));
    },
    [addErrorToast, addValidationToast]
  );
};
